import React, { Component } from 'react'
import PropTypes from 'prop-types'
import sortBy from 'sort-by'
import moment from 'moment'
import {
  Spin,
  Table,
  Column,
  Checkbox,
  Dropdown,
  Menu,
  Icon,
} from './common/Ant'
import { Anchor } from './common/Anchor'
import { Bread, Crumb } from './common/Bread'
import Divider from './common/Divider'
import Label from './common/Label'
import SearchFilter from './common/SearchFilter'
import Select, { Option } from './common/Select'
import { createQueryString } from '../utils/queryParams'
import { escapeRegExp } from '../utils/helpers'
import { ALL, SITE_STATUS, VIEW_ALL } from '../constants'
import { toTitleCase, renderSiteStatus } from '../utils/textFormatters'
import api from '../services/api'
import { downloadBillingReportExport } from '../services/billingReportExport'

const EMPTY_QUERY = {}

const BILLING_COUNT_KEYS = [
  'gateway',
  'smart',
  'leak',
  'evac',
  'meter',
  'valve',
]

const BILLING_COLUMN_TITLES = {
  gateway: 'Gateway',
  smart: 'Smart',
  leak: 'Leak',
  evac: 'Evac',
  meter: 'Meter',
  valve: 'Valve',
}

class BillingReport extends Component {
  static propTypes = {
    baseUrl: PropTypes.string.isRequired,
  }

  state = {
    allSites: [],
    selectedSiteSlugs: [],
    status: [SITE_STATUS.ACTIVE, SITE_STATUS.INSTALLING, SITE_STATUS.UNINSTALL],
    isLoadingSites: true,
    isExporting: false,
    exportError: null,
    siteSearchText: '',
    sortedInfo: {},
  }

  async componentDidMount() {
    await this.loadSitesForStatusFilter(this.state.status)
  }

  getStatusesForFilter = statusFilter => {
    const selectedValues = statusFilter || []
    const selectedStatuses = selectedValues.filter(x => x !== ALL)
    if (selectedStatuses.length === 0) {
      return Object.values(SITE_STATUS)
    }
    return selectedStatuses
  }

  loadSitesForStatusFilter = async statusFilter => {
    this.setState({ isLoadingSites: true })
    try {
      const statuses = this.getStatusesForFilter(statusFilter)
      const response = await api.getSitesBillingSummary(
        createQueryString({
          status: statuses,
        })
      )
      const allSites = (response.items || []).sort(sortBy('name'))
      const selectedSiteSlugs = allSites.map(s => s.slug)

      this.setState({
        allSites,
        selectedSiteSlugs,
      })
    } finally {
      this.setState({ isLoadingSites: false })
    }
  }

  getBillingCount = (record, key) => {
    const value = record && record[key]
    return typeof value === 'number' ? value : undefined
  }

  handleStatusChange = value => {
    const selectedValues = value || []
    const hasViewAll = selectedValues.indexOf(ALL) > -1
    const selectedStatuses = selectedValues.filter(x => x !== ALL)
    const isViewAll = selectedStatuses.length === 0
    const newValue =
      hasViewAll && selectedStatuses.length > 0 ? selectedStatuses : isViewAll ? [ALL] : selectedStatuses

    this.setState({
      status: newValue,
    })
    this.loadSitesForStatusFilter(newValue)
  }

  setSiteSearchText = siteSearchText => this.setState({ siteSearchText })

  noopFetch = () => {}

  filterSitesBySearch = (sites, searchText) => {
    const q = (searchText || '').trim()
    if (!q) {
      return sites
    }
    const regex = new RegExp(escapeRegExp(q), 'i')
    return sites.filter(
      s =>
        (s.name && regex.test(s.name)) || (s.slug && regex.test(s.slug))
    )
  }

  sortColumnKeyMatches = (sortedInfo, columnKey) =>
    (sortedInfo && sortedInfo.columnKey === columnKey) ||
    (sortedInfo && sortedInfo.field === columnKey)

  handleTableChange = (pagination, filters, sorter) => {
    this.setState({ sortedInfo: sorter })
  }

  compareBillingCounts = (a, b, key) => {
    const fa = this.getBillingCount(a, key)
    const fb = this.getBillingCount(b, key)
    return (fa ?? -1) - (fb ?? -1)
  }

  sumBillingCounts = sites =>
    BILLING_COUNT_KEYS.reduce((totals, key) => {
      totals[key] = sites.reduce((sum, record) => {
        const value = this.getBillingCount(record, key)
        return sum + (typeof value === 'number' ? value : 0)
      }, 0)
      return totals
    }, {})

  hasZeroAssets = record =>
    BILLING_COUNT_KEYS.every(key => {
      const value = this.getBillingCount(record, key)
      return !value
    })

  toggleSiteSelection = (siteSlug, checked) => {
    this.setState(prevState => {
      const { selectedSiteSlugs } = prevState
      if (checked) {
        if (selectedSiteSlugs.indexOf(siteSlug) > -1) {
          return null
        }
        return { selectedSiteSlugs: [...selectedSiteSlugs, siteSlug] }
      }
      return {
        selectedSiteSlugs: selectedSiteSlugs.filter(x => x !== siteSlug),
      }
    })
  }

  renderCount = value =>
    value === 0 || value ? value : '--'

  buildExportQueryString = () => {
    const { selectedSiteSlugs, allSites, siteSearchText, status } = this.state
    const statuses = this.getStatusesForFilter(status)
    const q = (siteSearchText || '').trim()
    const visibleSites = this.filterSitesBySearch(allSites, siteSearchText)
    const selectedSet = new Set(selectedSiteSlugs)
    const exportSlugs = visibleSites
      .filter(s => selectedSet.has(s.slug))
      .map(s => s.slug)

    const params = { status: statuses }
    if (q) {
      params.search = q
    }
    const needsSlugFilter =
      exportSlugs.length !== allSites.length ||
      (q && exportSlugs.length !== visibleSites.length)
    if (needsSlugFilter) {
      params.siteSlug = exportSlugs
    }
    return createQueryString(params)
  }

  handleExportMenuClick = async ({ key }) => {
    if (key !== 'csv' && key !== 'pdf') {
      return
    }
    this.setState({ isExporting: true, exportError: null })
    try {
      await downloadBillingReportExport(key, this.buildExportQueryString())
    } catch (err) {
      const message =
        (err && err.apiError && err.apiError.message) ||
        'Export failed. Please try again.'
      this.setState({ exportError: message })
    } finally {
      this.setState({ isExporting: false })
    }
  }

  render() {
    const { baseUrl } = this.props
    const {
      selectedSiteSlugs,
      status,
      allSites,
      isLoadingSites,
      isExporting,
      exportError,
      siteSearchText,
      sortedInfo,
    } = this.state
    const sortedSites = allSites
    const visibleSites = this.filterSitesBySearch(sortedSites, siteSearchText)
    const billingTotals = this.sumBillingCounts(visibleSites)
    const siteCount = visibleSites.length
    const siteCountLabel =
      siteCount === 1 ? '1 site' : `${siteCount} sites`
    const emptyText =
      siteSearchText.trim() && visibleSites.length === 0
        ? 'No sites match your search.'
        : 'No sites match this filter.'
    return (
      <section className="mb5 BillingReport">
        <div className="mb3">
          <Bread>
            <Crumb>
              <Anchor to={baseUrl}>Internal Reports</Anchor>
            </Crumb>
            <Crumb>Billing Report</Crumb>
          </Bread>
        </div>
        <Divider />
        <div className="flex-l">
          <div className="w-100 w-50-l pr3-l mb3 mb0-l">
            <Select
              label="Status"
              placeholder="Status"
              mode="multiple"
              input={{
                value: status,
                onChange: this.handleStatusChange,
              }}
              filterable
            >
              {[undefined, ...Object.keys(SITE_STATUS)].map(x => (
                <Option value={SITE_STATUS[x] || 'all'} key={x || 'all'}>
                  {toTitleCase(x) || VIEW_ALL}
                </Option>
              ))}
            </Select>
          </div>
          <div className="w-100 w-50-l mb3">
            <Label>Report date</Label>
            <div className="db black-80 lh-copy pt1">
              {moment().format('MMM D, YYYY')}
            </div>
          </div>
        </div>
        <div className="mb3">
          <SearchFilter
            searchText={siteSearchText}
            setSearchText={this.setSiteSearchText}
            getItems={this.noopFetch}
            query={EMPTY_QUERY}
            placeholder="Search sites"
          />
        </div>
        {isLoadingSites ? (
          <Spin size="large" className="w-100 center mv4" />
        ) : (
          <div>
            {exportError ? (
              <p className="red mb2 lh-copy">{exportError}</p>
            ) : null}
            <div className="mt3 flex justify-between items-center mb2">
              <p className="mb0 black-70 lh-copy">{siteCountLabel}</p>
              <Dropdown
                overlay={
                  <Menu onClick={this.handleExportMenuClick}>
                    <Menu.Item key="csv" disabled={isExporting}>
                      CSV (.csv)
                    </Menu.Item>
                    <Menu.Item key="pdf" disabled={isExporting}>
                      PDF (.pdf)
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
                disabled={isExporting}
              >
                <a
                  className={`ant-dropdown-link${
                    isExporting ? ' o-50 pointer-events-none' : ' pointer'
                  }`}
                >
                  <Icon type="download" className="mr1" />
                  {isExporting ? 'Preparing…' : 'Download'}
                  <Icon type="down" />
                </a>
              </Dropdown>
            </div>
            <div>
              <Table
                bordered
                dataSource={visibleSites}
                rowKey="slug"
                pagination={false}
                tableLayout="auto"
                locale={{ emptyText }}
                onChange={this.handleTableChange}
                rowClassName={record =>
                  this.hasZeroAssets(record) ? 'BillingReport-zero-assets' : ''
                }
                summary={
                  siteCount === 0
                    ? undefined
                    : () => (
                        <Table.Summary>
                          <Table.Summary.Row className="BillingReport-totals">
                            <Table.Summary.Cell index={0} />
                            <Table.Summary.Cell index={1}>
                              Total
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} />
                            {BILLING_COUNT_KEYS.map((key, index) => (
                              <Table.Summary.Cell index={3 + index} key={key}>
                                {this.renderCount(billingTotals[key])}
                              </Table.Summary.Cell>
                            ))}
                          </Table.Summary.Row>
                        </Table.Summary>
                      )
                }
              >
                <Column
                  title=""
                  width={60}
                  render={(text, record) => (
                    <Checkbox
                      checked={selectedSiteSlugs.indexOf(record.slug) > -1}
                      onChange={ev =>
                        this.toggleSiteSelection(record.slug, ev.target.checked)
                      }
                    />
                  )}
                />
                <Column
                  key="name"
                  title="Name"
                  dataIndex="name"
                  sorter={sortBy('name')}
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'name') &&
                    sortedInfo.order
                  }
                  render={(text, record) => (
                    <Anchor to={`/sites/${record.slug}/deployments`}>
                      {text}
                    </Anchor>
                  )}
                />
                <Column
                  key="status"
                  title="Status"
                  dataIndex="status"
                  sorter={(a, b) =>
                    String(
                      renderSiteStatus(a.status) || a.status || ''
                    ).localeCompare(
                      String(
                        renderSiteStatus(b.status) || b.status || ''
                      ),
                      undefined,
                      { sensitivity: 'base' }
                    )
                  }
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'status') &&
                    sortedInfo.order
                  }
                  render={text => renderSiteStatus(text)}
                />
                {BILLING_COUNT_KEYS.map(key => (
                  <Column
                    key={key}
                    title={BILLING_COLUMN_TITLES[key]}
                    sorter={(a, b) => this.compareBillingCounts(a, b, key)}
                    sortOrder={
                      this.sortColumnKeyMatches(sortedInfo, key) &&
                      sortedInfo.order
                    }
                    render={(text, record) =>
                      this.renderCount(this.getBillingCount(record, key))
                    }
                  />
                ))}
              </Table>
            </div>
          </div>
        )}
      </section>
    )
  }
}

export default BillingReport
